import { useQuery } from '@tanstack/react-query';

import { useCopy } from '../../../shared/lib/use-copy';
import { Button } from '../../../shared/ui/button';
import { CodeView } from '../../../shared/ui/code-view';
import { Card } from '../../../shared/ui/layout';
import { Note } from '../../../shared/ui/note';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { exportPresetVersion, specText } from '../../../entities/auth-preset';

export const PresetExport = ({
  presetId,
  version,
  fileKey,
}: {
  presetId: string;
  version: number;
  fileKey: string;
}) => {
  const { copied, copy } = useCopy();
  const exported = useQuery({
    queryKey: ['auth-preset-export', presetId, version],
    queryFn: () => exportPresetVersion(presetId, version),
  });

  const text = exported.data ? specText(exported.data) : '';

  const download = () => {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileKey}-v${version}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="p-0">
      <div className="px-4 pt-4">
        <h2 className="text-brand font-semibold text-primary">Export v{version}</h2>
        <p className="mt-2 text-data text-muted">
          One version, as a file you can hand to a colleague or load into another installation.
        </p>
      </div>

      <div className="px-4 py-4">
        {exported.isPending && <LoadingState rows={4} className="border-0" />}
        {exported.isError && <ErrorState error={exported.error} />}
        {exported.data && (
          <CodeView value={text} className="max-h-body rounded-panel border border-border-sunken" />
        )}
      </div>

      <Note className="mx-4">
        <span className="text-data leading-20 text-primary">No secret is in here.</span>
        <span className="mt-2 block">
          A scheme says how to check a signature. What to check it with lives on the source and does
          not travel.
        </span>
      </Note>

      <div className="mt-4 flex items-center gap-inset bg-field-muted px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={!text}
          onClick={() => void copy(text)}
        >
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
        <Button size="sm" disabled={!text} onClick={download}>
          Download
        </Button>
      </div>
    </Card>
  );
};
